import { useState, useEffect, useMemo } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import {
  buildLibraryMap,
  getEffectiveGroups,
} from "../muscleVolume";
import {
  colorFor,
  summarizeProgram,
  buildChartData,
} from "./programComparisonHelpers";

export default function ProgramComparisonDialog({
  open,
  onOpenChange,
  programs,
  workouts,
  exerciseLibrary,
  userMappings,
}) {
  // Default selection: first 2 programs (or all if there's only 1).
  // Re-initialize whenever the dialog is opened so we always start with a
  // sensible pre-selection regardless of the load order of `programs`.
  const [selectedIds, setSelectedIds] = useState([]);

  useEffect(() => {
    if (!open) return;
    setSelectedIds(programs.slice(0, Math.min(2, programs.length)).map((p) => p.id));
  }, [open, programs]);

  const libMap = useMemo(
    () => buildLibraryMap(exerciseLibrary, userMappings),
    [exerciseLibrary, userMappings]
  );
  const groupsAll = useMemo(() => getEffectiveGroups(userMappings), [userMappings]);
  const tallyOpts = useMemo(
    () => ({
      countSecondary: userMappings?.count_secondary !== false,
      secondaryWeight:
        typeof userMappings?.secondary_weight === "number"
          ? userMappings.secondary_weight
          : 0.5,
    }),
    [userMappings]
  );

  const summaries = useMemo(() => {
    const map = new Map(programs.map((p) => [p.id, p]));
    return selectedIds
      .map((id) => map.get(id))
      .filter(Boolean)
      .map((p) => summarizeProgram(p, workouts, libMap, tallyOpts));
  }, [selectedIds, programs, workouts, libMap, tallyOpts]);

  // Build chart data: rows = muscle groups present in any selected program,
  // cols = each program (data key = program id, name = program title)
  const chartData = useMemo(() => buildChartData(summaries, groupsAll), [summaries, groupsAll]);

  const toggleProgram = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="bg-[#171717] border-[#262626] text-white max-w-5xl max-h-[90vh] overflow-y-auto"
        data-testid="program-comparison-dialog"
      >
        <DialogHeader>
          <DialogTitle
            className="font-bold tracking-tight uppercase"
            style={{ fontFamily: "Barlow Condensed, sans-serif" }}
          >
            CONFRONTA PROGRAMMI
          </DialogTitle>
          <p className="text-xs text-zinc-500">
            Seleziona 2 o più programmi per confrontare volume per gruppo muscolare,
            set totali e ripetizioni totali pianificate. Utile per pianificare
            progressioni e deload.
          </p>
        </DialogHeader>

        {/* Program picker */}
        <div className="mt-2">
          <p className="label-overline mb-2">PROGRAMMI</p>
          <div className="flex flex-wrap gap-2" data-testid="program-comparison-picker">
            {programs.map((p) => {
              const active = selectedIds.includes(p.id);
              const idx = selectedIds.indexOf(p.id);
              const accent = active ? colorFor(idx) : "#3a3a3a";
              return (
                <button
                  key={p.id}
                  type="button"
                  onClick={() => toggleProgram(p.id)}
                  className="text-xs font-semibold px-3 py-1.5 rounded-full border transition-colors flex items-center gap-2"
                  style={{
                    backgroundColor: active ? `${accent}26` : "#0A0A0A",
                    borderColor: active ? accent : "#262626",
                    color: active ? accent : "#a1a1aa",
                  }}
                  data-testid={`program-comparison-toggle-${p.id}`}
                >
                  <span
                    className="w-2 h-2 rounded-full"
                    style={{ backgroundColor: active ? accent : "#3a3a3a" }}
                  />
                  {p.title}
                </button>
              );
            })}
          </div>
        </div>

        {summaries.length < 2 ? (
          <div className="mt-6 text-center py-10 bg-[#0A0A0A] border border-[#1A1A1A] rounded-lg">
            <p className="text-sm text-zinc-400">
              Seleziona almeno 2 programmi per vedere il confronto.
            </p>
          </div>
        ) : (
          <>
            {/* Summary table */}
            <div className="mt-6 overflow-x-auto" data-testid="program-comparison-summary">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[#262626]">
                    <th className="text-left py-2 pr-4 text-zinc-500 text-[11px] uppercase tracking-wider font-bold">
                      Programma
                    </th>
                    <th className="text-right py-2 px-3 text-zinc-500 text-[11px] uppercase tracking-wider font-bold">
                      Schede
                    </th>
                    <th className="text-right py-2 px-3 text-zinc-500 text-[11px] uppercase tracking-wider font-bold">
                      Esercizi
                    </th>
                    <th className="text-right py-2 px-3 text-zinc-500 text-[11px] uppercase tracking-wider font-bold">
                      Set totali
                    </th>
                    <th className="text-right py-2 pl-3 text-zinc-500 text-[11px] uppercase tracking-wider font-bold">
                      Reps totali
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {summaries.map((s, i) => (
                    <tr
                      key={s.id}
                      className="border-b border-[#1A1A1A]"
                      data-testid={`program-comparison-row-${s.id}`}
                    >
                      <td className="py-2.5 pr-4 flex items-center gap-2">
                        <span
                          className="w-2.5 h-2.5 rounded-full flex-shrink-0"
                          style={{ backgroundColor: colorFor(i) }}
                        />
                        <span className="text-white font-medium">{s.title}</span>
                      </td>
                      <td className="py-2.5 px-3 text-right text-zinc-300 tabular-nums">
                        {s.workoutsCount}
                      </td>
                      <td className="py-2.5 px-3 text-right text-zinc-300 tabular-nums">
                        {s.exerciseCount}
                      </td>
                      <td className="py-2.5 px-3 text-right text-zinc-300 tabular-nums">
                        {s.totalSets}
                      </td>
                      <td className="py-2.5 pl-3 text-right text-zinc-300 tabular-nums">
                        {s.totalReps.toLocaleString("it-IT")}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Per-muscle volume chart */}
            <div className="mt-6">
              <div className="flex items-baseline justify-between mb-2">
                <p className="label-overline">VOLUME PER GRUPPO MUSCOLARE (set settimanali)</p>
                <span className="text-[10px] text-zinc-600">
                  Include muscoli secondari (peso × {tallyOpts.secondaryWeight})
                </span>
              </div>
              {chartData.length === 0 ? (
                <p className="text-zinc-500 text-sm py-6 text-center">
                  Nessun esercizio mappato a un gruppo muscolare nei programmi selezionati.
                </p>
              ) : (
                <div data-testid="program-comparison-chart">
                  <ResponsiveContainer width="100%" height={Math.max(280, chartData.length * 38)}>
                    <BarChart data={chartData} layout="vertical" barGap={2} barCategoryGap={10}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#262626" horizontal={false} />
                      <XAxis type="number" stroke="#555" tick={{ fontSize: 11 }} />
                      <YAxis
                        type="category"
                        dataKey="muscle"
                        stroke="#888"
                        tick={{ fontSize: 12 }}
                        width={110}
                      />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: "#171717",
                          border: "1px solid #262626",
                          borderRadius: 6,
                          fontSize: 12,
                        }}
                        labelStyle={{ color: "#999" }}
                        cursor={{ fill: "#ffffff08" }}
                      />
                      <Legend wrapperStyle={{ fontSize: 12 }} />
                      {summaries.map((s, i) => (
                        <Bar
                          key={s.id}
                          dataKey={s.id}
                          name={s.title}
                          fill={colorFor(i)}
                          radius={[0, 3, 3, 0]}
                        />
                      ))}
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>

            {/* Quick deltas vs first selected program (baseline) */}
            {summaries.length >= 2 && (
              <div className="mt-6">
                <p className="label-overline mb-2">
                  DELTA VS BASELINE «{summaries[0].title}»
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                  {summaries.slice(1).map((s, i) => {
                    const base = summaries[0];
                    const dSets = s.totalSets - base.totalSets;
                    const dReps = s.totalReps - base.totalReps;
                    const pctSets = base.totalSets > 0
                      ? Math.round((dSets / base.totalSets) * 100)
                      : 0;
                    return (
                      <div
                        key={s.id}
                        className="bg-[#0A0A0A] border border-[#1A1A1A] rounded-lg p-3"
                        data-testid={`program-comparison-delta-${s.id}`}
                      >
                        <div className="flex items-center gap-2 mb-1.5">
                          <span
                            className="w-2 h-2 rounded-full"
                            style={{ backgroundColor: colorFor(i + 1) }}
                          />
                          <span className="text-xs text-white font-medium truncate">
                            {s.title}
                          </span>
                        </div>
                        <div className="flex items-baseline gap-3 text-[11px]">
                          <span className="text-zinc-500">Set:</span>
                          <span
                            className={`font-semibold ${
                              dSets > 0
                                ? "text-[#34C759]"
                                : dSets < 0
                                ? "text-[#FF9500]"
                                : "text-zinc-400"
                            }`}
                          >
                            {dSets >= 0 ? "+" : ""}
                            {dSets} ({pctSets >= 0 ? "+" : ""}
                            {pctSets}%)
                          </span>
                        </div>
                        <div className="flex items-baseline gap-3 text-[11px]">
                          <span className="text-zinc-500">Reps:</span>
                          <span
                            className={`font-semibold ${
                              dReps > 0
                                ? "text-[#34C759]"
                                : dReps < 0
                                ? "text-[#FF9500]"
                                : "text-zinc-400"
                            }`}
                          >
                            {dReps >= 0 ? "+" : ""}
                            {dReps.toLocaleString("it-IT")}
                          </span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        )}

        <div className="flex justify-end mt-4 pt-3 border-t border-[#262626]">
          <Button
            onClick={() => onOpenChange(false)}
            className="bg-[#007AFF] hover:bg-[#3395FF] text-white"
            data-testid="program-comparison-close"
          >
            Chiudi
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
